import { usePlaeeOrderBookGetOrderBook } from '@/lib/endpoints';
import { accumulateDepth, type DepthLevel } from './order-book-depth';
import { useLiveOrderBook } from './useLiveOrderBook';

export interface OutcomeOrderBookProps {
  /** Plaee external outcome id whose book to snapshot. */
  outcomeId: string;
  /** Local market id of the outcome — resolves the live Centrifugo channel. */
  marketId?: string;
  /** Plaee external market id of the outcome — the Centrifugo channel key. */
  externalMarketId?: string;
}

/** How many price levels to show per side. */
const DEPTH = 7;

/** Dollars (0–1) → whole-cent label, e.g. `0.61` → `61¢`. */
function centsLabel(dollars: number | undefined): string {
  return dollars == null ? '—' : `${Math.round(dollars * 100)}¢`;
}

/** Share quantity with thousands separators, e.g. `13218.22` → `13,218.22`. */
function sharesLabel(quantity: number | undefined): string {
  return quantity == null ? '—' : quantity.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** Notional dollars as `$X,XXX.XX`. */
function totalLabel(total: number): string {
  return `$${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Order Book snapshot for a single outcome — shared by the binary market page
 * (ARC-82 §5.4.3) and the snake page (ARC-84 §5).
 *
 * First-paint depth from `/plaee/orderbook/:outcomeId` (a live Plaee proxy keyed
 * on the **external** outcome id). Rendered only inside an expanded section, so
 * the fetch is lazy. When the market ids are supplied, {@link useLiveOrderBook}
 * overlays the Centrifugo `OrderBookUpdated` stream so the book stays current
 * without refetching; otherwise it stays snapshot-only.
 *
 * Layout follows the Crown Prediction design (Figma node 1828:55347) and the
 * standard prediction-market book (cf. Polymarket): asks (red) above the
 * Last/Spread divider and bids (green) below, prices descending top→bottom so
 * best ask meets best bid at the divider. The "Trade Up" depth bar and the
 * Total column are cumulative away from the spread — Total is the notional
 * cost to sweep the book through that level.
 */
export function OutcomeOrderBook({ outcomeId, marketId, externalMarketId }: OutcomeOrderBookProps) {
  const { data, isLoading, isError } = usePlaeeOrderBookGetOrderBook(outcomeId, {
    // The book moves fast — keep the first-paint snapshot fresh (the global 60s
    // default is far too stale here) so a reopen within the window still
    // re-seeds the live overlay from a near-current book.
    query: { enabled: Boolean(outcomeId), staleTime: 5_000 },
  });
  const live = useLiveOrderBook(data, outcomeId, marketId, externalMarketId);

  if (!outcomeId) return <p className="text-sm text-ink-muted">No order book for this market.</p>;
  if (isLoading) return <p className="text-sm text-ink-muted">Loading order book…</p>;
  if (isError || !data) return <p className="text-sm text-ink-muted">The order book is unavailable right now.</p>;

  // Prefer the live-merged book; the snapshot is the fallback. Both arrive
  // best-first, so take the DEPTH levels nearest the spread on each side and
  // accumulate depth away from it (standard book semantics: bars and Total
  // grow monotonically from the spread outward). Asks then reverse so the
  // whole book reads high→low, best ask meeting best bid at the divider.
  const book = live ?? { asks: data.asks, bids: data.bids, spread: data.spread, lastPrice: data.lastPrice };
  const askDepth = accumulateDepth(book.asks.slice(0, DEPTH));
  const bidDepth = accumulateDepth(book.bids.slice(0, DEPTH));

  // Depth bars scale to the deepest cumulative level on either side (the last
  // accumulated entry), so each side reads as a staircase widening away from
  // the spread. Computed before the display reverse so "last = deepest" holds
  // symmetrically for both sides.
  const maxCumulative = Math.max(
    1,
    askDepth[askDepth.length - 1]?.cumulativeQuantity ?? 0,
    bidDepth[bidDepth.length - 1]?.cumulativeQuantity ?? 0,
  );
  const asks = askDepth.reverse();
  const bids = bidDepth;

  return (
    <table className="w-full table-fixed overflow-hidden rounded-[10px] border border-ink-muted/30 border-separate border-spacing-0 text-left text-[12px] tracking-[-0.28px] lg:text-[14px]">
      <colgroup>
        <col className="w-[31%]" />
        <col className="w-[19%]" />
        <col className="w-[24%]" />
        <col className="w-[26%]" />
      </colgroup>
      <thead>
        <tr className="font-normal text-ink-muted/70">
          <th className="border-b border-ink-muted/30 py-2 pl-3 font-normal">Trade Up</th>
          <th className="border-b border-ink-muted/30 py-2 pr-2 font-normal">Price</th>
          <th className="border-b border-ink-muted/30 py-2 pr-2 font-normal">Shares</th>
          <th className="border-b border-ink-muted/30 py-2 pr-3 font-normal">Total</th>
        </tr>
      </thead>

      <tbody>
        {asks.map((e, i) => (
          <DepthRow key={`ask-${i}`} entry={e} tone="ask" maxCumulative={maxCumulative} />
        ))}
      </tbody>

      <tbody>
        <tr className="text-ink-muted">
          <td colSpan={4} className="border-y border-ink-muted/30 py-2 pl-3 pr-3">
            <span className="mr-6">
              Last: <span className="font-bold text-ink-muted">{centsLabel(book.lastPrice)}</span>
            </span>
            <span>
              Spread: <span className="font-bold text-ink-muted">{centsLabel(book.spread)}</span>
            </span>
          </td>
        </tr>
      </tbody>

      <tbody>
        {bids.map((e, i) => (
          <DepthRow key={`bid-${i}`} entry={e} tone="bid" maxCumulative={maxCumulative} />
        ))}
      </tbody>
    </table>
  );
}

/**
 * One depth level (`<tr>`): a left-anchored Trade Up bar (sized by cumulative
 * share depth from the spread) filling the first cell, the price (tinted by
 * side), the level's share quantity, and the cumulative notional Total —
 * the cost to sweep the book through this level.
 */
function DepthRow({ entry, tone, maxCumulative }: { entry: DepthLevel; tone: 'ask' | 'bid'; maxCumulative: number }) {
  const isAsk = tone === 'ask';
  // Bar fills the Trade Up cell: shallowest level ~26%, deepest caps at 90%.
  const widthPct = Math.min(90, 26 + (entry.cumulativeQuantity / maxCumulative) * 64);

  return (
    <tr className="h-[25px]">
      <td className="relative p-0">
        <div
          aria-hidden
          className={`absolute inset-y-px left-0 rounded-r-[5px] ${isAsk ? 'bg-down/20' : 'bg-up/20'}`}
          style={{ width: `${widthPct}%` }}
        />
      </td>
      <td className={`py-px pr-2 font-semibold ${isAsk ? 'text-down' : 'text-up'}`}>{centsLabel(entry.price)}</td>
      <td className="py-px pr-2 text-ink">{sharesLabel(entry.quantity)}</td>
      <td className="py-px pr-3 text-ink">{totalLabel(entry.cumulativeTotal)}</td>
    </tr>
  );
}
